import React, { useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { makeStyles, Grid, IconButton, ListItem, Tooltip, ListItemText } from '@material-ui/core'
import ArrowBack from '@material-ui/icons/ArrowBack'

import ProductImageThumb from '~/components/ProductImageThumb'
import useViewportSize from '~/components/useViewportSize'
import { SearchProductVirtualized } from '~/components/Header/SearchProductVirtualized'
import { useLiterals } from '~/components/Literals'

import { TYPES } from '~/actions'

const useStyles = makeStyles((theme) => ({
  wrapper: {
    zIndex: 500,
    width: '100%',
  },
  content: {
    color: theme.palette.secondary.contrastText,
    background: theme.palette.secondary.main,

    gap: 8,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',

    '& > div': {
      height: 56,
      alignItems: 'center',
      display: 'flex',

      [theme.breakpoints.up('md')]: {
        height: 77,
      },

      '&.contentSearch': {
        width: '100%',
        padding: 16,
      },
      '&.headerBgMobile': {
        margin: '16px 8px',
      },
    },
    '& > div:nth-child(2)': {
      width: '100%',
    },
  },
  text: {
    padding: '0 16px',
    '& span, & p': {
      color: theme.palette.secondary.contrastText,
      ...theme.lineLimit(),
      maxHeight: 27,
    },
    '& p': {
      maxHeight: 22,
    },
  },
  listItem: {
    padding: 0,
    [theme.breakpoints.up('md')]: {
      padding: 8,
    },
  },
}))

function ProductHeader({ product, loading, view, handleToggle, position, config }) {
  const classes = useStyles()
  const history = useHistory()
  const viewport = useViewportSize()
  const { getLiteral } = useLiterals()
  const location = useLocation()

  const urlDomain = window.location.toString()
  const isHideProductHeader = urlDomain.includes('produto/todos') || urlDomain.includes('loja/')

  const withProductDialog = !loading && product?.gtin !== 'all'
  const isMobile = viewport.width < 930

  const handleClick = () => {
    if (product.gtin === 'all') {
      history.goBack()
      return
    }

    history.push(`/produto/${product.gtin}/detalhes`, {
      fromApp: true,
      prevPath: location.pathname,
    })
  }

  const handleBack = useCallback(() => {
    if (view === TYPES.VIEW_STORE_DETAILS) {
      if (handleToggle) handleToggle()
      history.goBack()
    } else if (view === TYPES.VIEW_STORES && history?.location?.state?.from) {
      history.push(history.location.state.from)
    } else {
      history.push('/')
    }
  }, [view, history, handleToggle])

  const showArrowBack = () => {
    const isViewContext = Boolean(config.showContextViewStore)

    if (isViewContext) return view === TYPES.VIEW_STORE_DETAILS

    return [TYPES.VIEW_STORE_DETAILS, TYPES.VIEW_STORES].includes(view)
  }

  if (isHideProductHeader && Boolean(config.showContextViewEstablishment)) return null

  return (
    <Grid className={classes.wrapper}>
      <Grid className={classes.content}>
        {showArrowBack() && (
          <Grid>
            <Tooltip id="tooltip-back" title={getLiteral('1051')} placement="bottom-end">
              <IconButton
                size="small"
                color="inherit"
                aria-label="go back"
                disabled={loading}
                onClick={handleBack}
                data-cy="backButton"
                style={{ marginLeft: 4 }}
              >
                <ArrowBack />
              </IconButton>
            </Tooltip>
          </Grid>
        )}

        <Grid
          {...(config.showContextViewStore && {
            className: `contentSearch ${isMobile && view === TYPES.VIEW_STORES ? 'headerBgMobile' : ''}`,
          })}
        >
          {product && Boolean(config.showContextViewStore) && view !== TYPES.VIEW_STORE_DETAILS ? (
            <Grid style={{ width: '100%', backgroundColor: '#fff', borderRadius: 4 }}>
              <SearchProductVirtualized product={product} position={position} config={config} />
            </Grid>
          ) : (
            product && (
              <ListItem
                className={classes.listItem}
                button={withProductDialog}
                onClick={handleClick}
                data-cy="product-header"
              >
                <ProductImageThumb product={product} />
                <ListItemText className={classes.text} primary={product.name} secondary={product.description} />
              </ListItem>
            )
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default ProductHeader
